import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCoin } from '../CoinContext';

const getPath = (path) => {
  const isCampSubdomain = window.location.hostname === 'camp.ssalsoongi.com';
  if (isCampSubdomain) {
    return path.replace(/^\/camp/, '') || '/';
  }
  return path;
};

const THEMES = {
  dark: {
    bg: '#111827',
    cardBg: '#1f2937',
    inputBg: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
    text: '#e5e7eb',
    textMuted: '#9ca3af',
    myBubble: '#667eea',
    myBubbleText: '#fff',
    theirBubble: '#374151',
    theirBubbleText: '#e5e7eb',
  },
  light: {
    bg: '#ffffff',
    cardBg: '#f3f4f6',
    inputBg: 'rgba(0,0,0,0.05)',
    border: 'rgba(0,0,0,0.1)',
    text: '#1a1a1a',
    textMuted: '#6b7280',
    myBubble: '#667eea',
    myBubbleText: '#fff',
    theirBubble: '#e5e7eb',
    theirBubbleText: '#1f2937',
  },
};

export default function CampChatModal({ isOpen, onClose, targetWallet, targetNickname, postId, postTitle, appliedClass }) {
  const navigate = useNavigate();
  const { walletAddress, nickname } = useCoin();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [roomId, setRoomId] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isPostAuthor, setIsPostAuthor] = useState(false);
  const [isRecruited, setIsRecruited] = useState(false);
  const messagesEndRef = useRef(null);
  const subscriptionRef = useRef(null);
  const [theme] = useState(() => {
    const savedMode = localStorage.getItem('campThemeMode');
    const savedTheme = localStorage.getItem('campTheme');
    if (savedMode === 'system' || !savedMode) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return savedTheme || 'dark';
  });

  const colors = THEMES[theme];

  // Initialize chat room
  useEffect(() => {
    if (!isOpen || !walletAddress || !targetWallet) return;

    initChatRoom();

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [isOpen, walletAddress, targetWallet]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const initChatRoom = async () => {
    setLoading(true);
    try {
      // Find existing room between these two users for this specific post
      let query = supabase
        .from('camp_chat_rooms')
        .select('*')
        .or(
          `and(user1_wallet.eq.${walletAddress},user2_wallet.eq.${targetWallet}),and(user1_wallet.eq.${targetWallet},user2_wallet.eq.${walletAddress})`
        );
      if (postId) {
        query = query.eq('post_id', postId);
      } else {
        query = query.is('post_id', null);
      }
      const { data: existingRooms, error: findError } = await query;

      if (findError) throw findError;

      let room;
      if (existingRooms && existingRooms.length > 0) {
        room = existingRooms[0];
        // Update applied_class if user is applying with a new class
        if (appliedClass && room.applied_class !== appliedClass) {
          await supabase
            .from('camp_chat_rooms')
            .update({ applied_class: appliedClass })
            .eq('id', room.id);
          room.applied_class = appliedClass;
        }
      } else {
        // Create new room
        const { data: newRoom, error: createError } = await supabase
          .from('camp_chat_rooms')
          .insert({
            user1_wallet: walletAddress,
            user1_nickname: nickname || `user_${walletAddress.slice(2, 8)}`,
            user2_wallet: targetWallet,
            user2_nickname: targetNickname || 'Anonymous',
            post_id: postId || null,
            post_title: postTitle || null,
            applied_class: appliedClass || null,
          })
          .select()
          .single();

        if (createError) throw createError;
        room = newRoom;
      }

      setRoomId(room.id);
      setRoomData(room);

      // Check if current user is the post author & if class is already recruited
      if (room.post_id) {
        try {
          const { data: postData } = await supabase
            .from('camp_posts')
            .select('author_wallet, recruited_classes')
            .eq('id', room.post_id)
            .single();
          if (postData) {
            setIsPostAuthor(postData.author_wallet === walletAddress);
            const rc = postData.recruited_classes || [];
            if (room.applied_class && rc.includes(room.applied_class)) {
              setIsRecruited(true);
            }
          }
        } catch (e) {
          console.error('Failed to check post author:', e);
        }
      }

      // Fetch existing messages
      const { data: msgs, error: msgError } = await supabase
        .from('camp_chat_messages')
        .select('*')
        .eq('room_id', room.id)
        .order('created_at', { ascending: true });

      if (msgError) throw msgError;
      setMessages(msgs || []);

      // Subscribe to new messages
      const channel = supabase
        .channel(`chat_room_${room.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'camp_chat_messages',
            filter: `room_id=eq.${room.id}`,
          },
          (payload) => {
            setMessages((prev) => {
              // Avoid duplicates (check real id and matching content for optimistic msgs)
              if (prev.some((m) => m.id === payload.new.id)) return prev;
              // Replace optimistic message if it matches
              const optimisticIdx = prev.findIndex(
                (m) => typeof m.id === 'string' && m.id.startsWith('temp_') &&
                  m.content === payload.new.content &&
                  m.sender_wallet === payload.new.sender_wallet
              );
              if (optimisticIdx !== -1) {
                const updated = [...prev];
                updated[optimisticIdx] = payload.new;
                return updated;
              }
              return [...prev, payload.new];
            });
          }
        )
        .subscribe();

      subscriptionRef.current = channel;
    } catch (err) {
      console.error('Failed to init chat room:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !roomId || sending) return;

    const msgText = newMessage.trim();
    const tempId = `temp_${Date.now()}`;
    const now = new Date().toISOString();
    const senderNickname = nickname || `user_${walletAddress.slice(2, 8)}`;

    // Optimistic update - show message immediately
    const optimisticMsg = {
      id: tempId,
      room_id: roomId,
      sender_wallet: walletAddress,
      sender_nickname: senderNickname,
      content: msgText,
      created_at: now,
    };
    setMessages((prev) => [...prev, optimisticMsg]);
    setNewMessage('');
    setSending(true);

    try {
      const { data, error } = await supabase.from('camp_chat_messages').insert({
        room_id: roomId,
        sender_wallet: walletAddress,
        sender_nickname: senderNickname,
        content: msgText,
      }).select().single();

      if (error) throw error;

      // Replace temp message with real one
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? data : m))
      );

      // Update room's last message
      await supabase
        .from('camp_chat_rooms')
        .update({
          last_message: msgText,
          last_message_at: new Date().toISOString(),
        })
        .eq('id', roomId);
    } catch (err) {
      console.error('Failed to send message:', err);
      // Remove optimistic message and restore input on failure
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setNewMessage(msgText);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteRoom = async () => {
    if (!roomId || !roomData) return;
    const isUser1 = roomData.user1_wallet === walletAddress;
    const myDeleted = isUser1 ? roomData.user1_deleted : roomData.user2_deleted;
    if (myDeleted) return;

    if (!window.confirm('Delete this chat room? Both users must delete for permanent removal.')) return;
    try {
      const updateField = isUser1 ? { user1_deleted: true } : { user2_deleted: true };
      const otherDeleted = isUser1 ? roomData.user2_deleted : roomData.user1_deleted;

      if (otherDeleted) {
        const { error: msgErr } = await supabase.from('camp_chat_messages').delete().eq('room_id', roomId);
        if (msgErr) throw msgErr;
        const { error: roomErr } = await supabase.from('camp_chat_rooms').delete().eq('id', roomId);
        if (roomErr) throw roomErr;
        onClose({ permanentlyDeleted: true });
      } else {
        const { data, error } = await supabase
          .from('camp_chat_rooms')
          .update(updateField)
          .eq('id', roomId)
          .select();
        if (error) throw error;
        if (!data || data.length === 0) {
          alert('Failed to update. RLS policy may be missing on camp_chat_rooms.');
          return;
        }
        setRoomData({ ...roomData, ...(isUser1 ? { user1_deleted: true } : { user2_deleted: true }) });
      }
    } catch (err) {
      console.error('Failed to delete chat room:', err);
      alert('Failed to delete chat room.');
    }
  };

  const handleRecruitStatusChange = async (newStatus) => {
    if (!roomData?.post_id || !roomData?.applied_class) return;

    try {
      const { data: postData, error: fetchErr } = await supabase
        .from('camp_posts')
        .select('recruited_classes')
        .eq('id', roomData.post_id)
        .single();
      if (fetchErr) throw fetchErr;

      let current = postData?.recruited_classes || [];
      if (newStatus === 'recruited') {
        if (!current.includes(roomData.applied_class)) {
          current.push(roomData.applied_class);
        }
      } else {
        current = current.filter(c => c !== roomData.applied_class);
      }

      const { data, error } = await supabase
        .from('camp_posts')
        .update({ recruited_classes: current })
        .eq('id', roomData.post_id)
        .eq('author_wallet', walletAddress)
        .select();
      if (error) throw error;
      if (!data || data.length === 0) {
        alert('업데이트 실패. RLS 정책을 확인해주세요.');
        return;
      }

      setIsRecruited(newStatus === 'recruited');
    } catch (err) {
      console.error('Failed to update recruit status:', err);
      alert('모집 상태 변경에 실패했습니다.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateSeparator = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Group messages by date
  const getDateKey = (dateString) => new Date(dateString).toLocaleDateString();

  if (!isOpen) return null;

  const displayName = targetNickname || 'Anonymous';

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: colors.bg,
          borderRadius: 16,
          width: '100%',
          maxWidth: 430,
          height: '80vh',
          maxHeight: 600,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Chat Header */}
        <div
          style={{
            padding: '14px 16px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: colors.text }}>
              {displayName}
            </span>
            {(roomData?.post_title || postTitle) && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  const pid = roomData?.post_id || postId;
                  if (pid) {
                    onClose();
                    navigate(getPath(`/camp/post/${pid}`));
                  }
                }}
                style={{ fontSize: 11, color: colors.textMuted, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(128,128,128,0.4)', textUnderlineOffset: 2 }}
              >
                {roomData?.post_title || postTitle}
              </span>
            )}
            {(roomData?.applied_class || appliedClass) && (
              <span style={{
                fontSize: 11, color: '#667eea', fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                Applied: {roomData?.applied_class || appliedClass}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {/* 모집 상태 드롭다운 (글 작성자 전용) */}
            {isPostAuthor && roomData?.applied_class && (
              <select
                value={isRecruited ? 'recruited' : 'recruiting'}
                onChange={(e) => handleRecruitStatusChange(e.target.value)}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '4px 6px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  outline: 'none',
                  border: isRecruited
                    ? '1px solid rgba(34,197,94,0.4)'
                    : `1px solid ${colors.border}`,
                  background: isRecruited
                    ? 'rgba(34,197,94,0.15)'
                    : colors.inputBg,
                  color: isRecruited ? '#22c55e' : colors.text,
                }}
              >
                <option value="recruiting">모집중</option>
                <option value="recruited">모집 완료</option>
              </select>
            )}
            {roomId && roomData && (() => {
              const isUser1 = roomData.user1_wallet === walletAddress;
              const myDeleted = isUser1 ? roomData.user1_deleted : roomData.user2_deleted;
              const otherDeleted = isUser1 ? roomData.user2_deleted : roomData.user1_deleted;
              return myDeleted ? (
                <span style={{
                  fontSize: 10, color: '#f44336', padding: '4px 8px',
                  background: 'rgba(244,67,54,0.1)', borderRadius: 8,
                }}>
                  {otherDeleted ? 'Deleting...' : 'Delete requested'}
                </span>
              ) : (
                <button
                  onClick={handleDeleteRoom}
                  style={{
                    background: otherDeleted ? 'rgba(244,67,54,0.2)' : 'rgba(244,67,54,0.1)',
                    border: `1px solid rgba(244,67,54,${otherDeleted ? '0.5' : '0.3'})`,
                    borderRadius: 8,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    color: '#f44336',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  Delete{otherDeleted ? ' (1/2)' : ''}
                </button>
              );
            })()}
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: colors.textMuted,
                cursor: 'pointer',
                fontSize: 22,
                padding: 4,
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, padding: 40 }}>
              Loading...
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, padding: 40, fontSize: 13 }}>
              Start a conversation with {displayName}
            </div>
          ) : (
            messages.map((msg, i) => {
              const isMe = msg.sender_wallet === walletAddress;
              const showDate =
                i === 0 || getDateKey(msg.created_at) !== getDateKey(messages[i - 1].created_at);

              return (
                <React.Fragment key={msg.id || i}>
                  {showDate && (
                    <div
                      style={{
                        textAlign: 'center',
                        fontSize: 11,
                        color: colors.textMuted,
                        margin: '12px 0 8px',
                      }}
                    >
                      {formatDateSeparator(msg.created_at)}
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: isMe ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-end',
                      gap: 6,
                    }}
                  >
                    {isMe && (
                      <span style={{ fontSize: 10, color: colors.textMuted, flexShrink: 0 }}>
                        {formatTime(msg.created_at)}
                      </span>
                    )}
                    <div
                      style={{
                        maxWidth: '70%',
                        padding: '8px 12px',
                        borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                        backgroundColor: isMe ? colors.myBubble : colors.theirBubble,
                        color: isMe ? colors.myBubbleText : colors.theirBubbleText,
                        fontSize: 13,
                        lineHeight: 1.5,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {msg.content}
                    </div>
                    {!isMe && (
                      <span style={{ fontSize: 10, color: colors.textMuted, flexShrink: 0 }}>
                        {formatTime(msg.created_at)}
                      </span>
                    )}
                  </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div
          style={{
            padding: '12px 16px',
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
            flexShrink: 0,
          }}
        >
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            style={{
              flex: 1,
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: '10px 14px',
              color: colors.text,
              fontSize: 13,
              outline: 'none',
              resize: 'none',
              maxHeight: 100,
              lineHeight: 1.4,
              fontFamily: 'inherit',
            }}
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            style={{
              background: newMessage.trim()
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : colors.cardBg,
              border: 'none',
              borderRadius: 12,
              padding: '10px 16px',
              cursor: newMessage.trim() ? 'pointer' : 'default',
              color: newMessage.trim() ? '#fff' : colors.textMuted,
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
              opacity: sending ? 0.6 : 1,
            }}
          >
            {sending ? '...' : 'Send'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
